import clsx from 'clsx'
import GeoContext from 'contexts/geoContext'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { getRegionCodeFromPathname } from 'lib/string/regionUtils'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { Link } from 'react-router-dom'
import type { LinkProps } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { scrollToId } from 'lib/window/scrollUtils'
import UrlLocation from 'src/lib/url/UrlLocation'

/*
  This component is to be used as a 'wrapper' for things that are clickable
  and link to another URL without imparting any styles to it.

  :where is used to reduce the specificity of the styles so that they can be overridden by other components
*/
const clickableElementStyles = css`
  :where(&) {
    display: block;
  }

  &.relative {
    position: relative;
  }
`

const clickMoveTolerance = 3
const hashScrollRetryDelayMs = 100
const hashScrollRetryCount = 10

const InternalHashLink = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const { onClick, target, to, ...rest } = props
  const pendingScrollTimeoutRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    return () => clearTimeout(pendingScrollTimeoutRef.current)
  }, [])

  const handleClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)

    if (
      event.defaultPrevented ||
      target === '_blank' ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.button !== 0
    ) {
      return
    }

    pendingScrollTimeoutRef.current = window.setTimeout(() => {
      const scrollToHashTarget = (attempt = 0) => {
        let hash: string | undefined

        if (typeof to === 'string') {
          const hashIndex = to.indexOf('#')
          hash = hashIndex >= 0 ? to.slice(hashIndex) : undefined
        } else {
          hash = to.hash
        }

        if (!hash) return
        if (hash === '#' || hash === '#top') {
          window.scrollTo(0, 0)
          return
        }

        const targetId = decodeURIComponent(hash.replace(/^#/, ''))
        if (document.getElementById(targetId)) {
          scrollToId(targetId)
        } else if (attempt < hashScrollRetryCount) {
          pendingScrollTimeoutRef.current = window.setTimeout(() => {
            scrollToHashTarget(attempt + 1)
          }, hashScrollRetryDelayMs)
        }
      }

      scrollToHashTarget()
    }, 0)
  }, [onClick, target, to])

  return <Link {...rest} ref={ref} target={target} to={to} onClick={handleClick} />
})

InternalHashLink.displayName = 'InternalHashLink'

const ClickableElement = styled.a`
  ${clickableElementStyles}
`

const ClickableInternalLink = styled(InternalHashLink)`
  ${clickableElementStyles}
`

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>, Omit<React.AnchorHTMLAttributes<HTMLAnchorElement | HTMLButtonElement>, 'type'> {
  to?: string
  preventSwipeClick?: boolean
  activeClassName?: string
  relative?: boolean
  ref?: React.Ref<HTMLAnchorElement>
}

function ClickableLink(props: Props) {
  const {
    to,
    href,
    onMouseDown,
    onClick,
    preventSwipeClick,
    target,
    rel,
    relative,
    className,
    activeClassName: _activeClassName,
    ...rest
  } = props

  const lastMouseDown = useRef({ x: -clickMoveTolerance, y: -clickMoveTolerance })
  const { currentRegionCode, locale } = useContext(GeoContext)
  const { languageOptions } = useLocaleUrlOptions()
  const urlLocale = locale ?? (
    typeof window === 'undefined' ? undefined : new UrlLocation(window.location.pathname).locale
  )

  const linkTo = useMemo(
    () => to ? buildUrl(to, currentRegionCode, undefined, undefined, {
      locale: urlLocale,
      languageOptions,
    }) : undefined,
    [currentRegionCode, languageOptions, to, urlLocale],
  )
  const linkHref = useMemo(() => {
    if (!href || !href.startsWith('/') || href.startsWith('//')) return href
    // href may already be prefixed with a different region (e.g. a region-switch link),
    // in which case it must not be rewritten with the current region.
    if (getRegionCodeFromPathname(href)) return href

    return buildUrl(href, currentRegionCode, undefined, undefined, {
      locale: urlLocale,
      languageOptions,
    })
  }, [currentRegionCode, href, languageOptions, urlLocale])

  const onLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!e.defaultPrevented) {
      if (preventSwipeClick) {
        const mouseMovementX = Math.abs(e.screenX - lastMouseDown.current.x)
        const mouseMovementY = Math.abs(e.screenY - lastMouseDown.current.y)
        if (mouseMovementX > clickMoveTolerance || mouseMovementY > clickMoveTolerance) {
          e.preventDefault()
          return
        }
      }
      onClick?.(e)
    }
  }, [onClick, preventSwipeClick])

  const onLinkMouseDown = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    onMouseDown?.(e)

    lastMouseDown.current = { x: e.screenX, y: e.screenY }
  }, [onMouseDown])

  const resolvedClassName = clsx(className, { relative })
  const resolvedRel = rel ?? (target === '_blank' ? 'noopener' : undefined)
  const resolvedOnMouseDown = preventSwipeClick ? onLinkMouseDown : onMouseDown

  if (to) {
    return <ClickableInternalLink
      {...rest}
      className={resolvedClassName}
      onClick={onLinkClick}
      onMouseDown={resolvedOnMouseDown}
      to={linkTo ?? ''}
      target={target ?? undefined}
      rel={resolvedRel}
    />
  }

  return <ClickableElement
    {...rest}
    className={resolvedClassName}
    onClick={onLinkClick}
    onMouseDown={resolvedOnMouseDown}
    href={linkHref}
    target={target ?? undefined}
    rel={resolvedRel}
  />
}

export default React.memo(ClickableLink)
